import { LinksFunction, LoaderFunctionArgs, json } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
} from "@remix-run/react";
import { getApi } from "@core/api";
import styles from "./tailwind.css?url";
import { createStyleFromTheme } from "@core/helpers";
import { Toaster } from "@core/components/ui/toaster";
import { DialogProvider } from "@core/components/ui/dialogprovider";
// These lines help ensure the font awesome icons render properly on the server without duplicating the CSS classes
// https://stackoverflow.com/questions/56334381/why-my-font-awesome-icons-are-being-displayed-big-at-first-and-then-updated-to-t
import "@fortawesome/fontawesome-svg-core/styles.css";
import { config, library } from "@fortawesome/fontawesome-svg-core";
import { fal } from "@fortawesome/pro-light-svg-icons";
import { ExtensionProvider, ExtensionRegistry } from "@core/components/PipelineComponents/ExtensionProvider";
import { IPxConfigSection } from "./apidtos";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DefaultErrorBoundary } from "@core/components/DefaultErrorBoundary";
import i18nServer, { localeCookie } from "./i18n.server";
import { useChangeLanguage } from "remix-i18next/react";
import { ThemeDebugOverlay } from "@core/components/ThemeDebugOverlay";
import { SelectedPatientProvider } from "@/overrides/med/components/SelectedPatientProvider";
import { InOfficeOrderingFromQuery } from "@/overrides/med/components/InOfficeOrderingFromQuery";
config.autoAddCss = false;
library.add(fal);

// Just dynamically import all the extension modules here in root.tsx so they can register themselves
const extensions = import.meta.glob("./**/*.ext.tsx", { eager: true });
// This will ensure that all extensions are loaded and registered
console.info("Loaded extensions:", Object.keys(extensions));

// configure CSS link to prevent duplicated CSS classes
export const links: LinksFunction = () => {
  return [
    { rel: "stylesheet", href: styles },
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
    {
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Outfit:wght@200;400;600&family=Bebas+Neue&display=swap",
    },
  ];
}

export async function loader(args: LoaderFunctionArgs) {
  const api = await getApi(args.request);
  const [ config, theme, locale ] = await Promise.all([
    api["/config"]() as Promise<Record<string, IPxConfigSection>>,
    api["/theme"](),
    i18nServer.getLocale(args.request),
  ]);
  return json(
    {
      config,
      theme,
      locale,
    },
    {
      headers: {
        "Cache-Control": "public, max-age=86400",
        "Set-Cookie": await localeCookie.serialize(locale),
      }
    }
  );
}

export const shouldRevalidate = () => false;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});

export function Layout({ children }: { children: React.ReactNode }) {
  const rootLoaderData = useRootLoaderData();
  const theme = rootLoaderData?.theme;

  useChangeLanguage(rootLoaderData?.locale || "en");
  return (
    <html lang={rootLoaderData?.locale || "en"}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {/* Dynamic favicon from Branding config (falls back to /api/branding/favicon) */}
        {(() => {
          const config = rootLoaderData?.config as Record<string, IPxConfigSection> | undefined;
          const branding = (config?.Branding ?? {}) as any;
          const rawUrl: string | undefined = branding?.FaviconUrl as any;
          const normalized = (() => {
            if (!rawUrl) return "/api/branding/favicon";
            if (rawUrl.startsWith("http")) return rawUrl;
            if (rawUrl.startsWith("/api/")) return rawUrl;
            if (rawUrl.startsWith("/branding/")) return `/api${rawUrl}`;
            if (rawUrl.startsWith("/")) return rawUrl; // other absolute path
            return `/api/${rawUrl}`;
          })();
          return (
            <>
              <link rel="icon" href={normalized} />
              <link rel="shortcut icon" href={normalized} />
            </>
          );
        })()}
      </head>
      <body style={createStyleFromTheme(theme)} className="dark:bg-black">
        <QueryClientProvider client={queryClient}>
          <ExtensionProvider>
            <SelectedPatientProvider>
              <DialogProvider />
              {children}
            </SelectedPatientProvider>
          </ExtensionProvider>
          <Toaster />
          <ThemeDebugOverlay />
        </QueryClientProvider>
        <ScrollRestoration />
        <Scripts />
        <script src="/spg-theme-assets/spg-theme-js.js" defer></script>
      </body>
    </html>
  );
}

export default function App() {
  return (
    <>
      <InOfficeOrderingFromQuery />
      <Outlet />
    </>
  );
}

export const useRootLoaderData = () => useRouteLoaderData<typeof loader>("root")!;

export function ErrorBoundary() {
  return <DefaultErrorBoundary />;
}
